import React, { useCallback, useState } from 'react';
import { useVenueClaims, VenueClaim } from './api/useVenueClaims';
import { Env, getAdminApiUrl, ENV_COLORS, Spinner, ErrorBanner, SuccessBanner } from '../shared';

// ─────────────────────────────────────────────────────────────────────────────
// Action Confirm Modal
// ─────────────────────────────────────────────────────────────────────────────

type PendingAction = { claim: VenueClaim; action: 'approve' | 'reject' };

function ClaimActionModal({
  pending,
  onClose,
  onConfirm,
  loading,
}: {
  pending: PendingAction;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}) {
  const isApprove = pending.action === 'approve';
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-gray-900 border border-gray-700 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm flex flex-col max-h-[92vh] sm:my-4">
        <div className="px-5 sm:px-6 py-5 sm:py-6 space-y-4 flex-1 overflow-y-auto">
          <div
            className={`w-12 h-12 mx-auto rounded-xl border flex items-center justify-center ${
              isApprove
                ? 'bg-emerald-950/50 border-emerald-800/50'
                : 'bg-red-950/50 border-red-800/50'
            }`}
          >
            {isApprove ? (
              <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </div>
          <div className="text-center">
            <h3 className="text-lg font-semibold text-white">
              {isApprove ? 'Approve venue claim' : 'Reject venue claim'}
            </h3>
            <p className="text-sm text-gray-400 mt-1">
              {isApprove ? (
                <>
                  This <span className="text-white font-medium">transfers ownership</span> of{' '}
                  <span className="text-white font-medium">{pending.claim.businessName}</span> to the
                  claimant and grants them business access. This cannot be undone here.
                </>
              ) : (
                <>
                  Reject the claim on{' '}
                  <span className="text-white font-medium">{pending.claim.businessName}</span>? No
                  ownership changes — the venue stays as-is.
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-3 px-5 sm:px-6 py-4 border-t border-gray-800 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-xl transition-all disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 ${
              isApprove ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {loading ? (
              <>
                <Spinner size="sm" />
                {isApprove ? 'Approving…' : 'Rejecting…'}
              </>
            ) : isApprove ? (
              'Approve & transfer'
            ) : (
              'Reject'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Venue-Claim Review Queue Tab
// ─────────────────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function VenueClaimsTab() {
  const [selectedEnv, setSelectedEnv] = useState<Env>('prod');
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [staleMsg, setStaleMsg] = useState<string | null>(null);

  const { claims, loading, error, refetch, approve, reject, actingId } = useVenueClaims(selectedEnv);

  const handleEnvChange = useCallback((env: Env) => {
    setSelectedEnv(env);
    setStaleMsg(null);
  }, []);

  const showSuccess = useCallback((msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!pending) return;
    const { claim, action } = pending;
    const outcome = action === 'approve' ? await approve(claim.id) : await reject(claim.id);
    if (outcome.ok) {
      setPending(null);
      setStaleMsg(null);
      showSuccess(
        action === 'approve'
          ? `${claim.businessName} transferred to the claimant.`
          : `Claim on ${claim.businessName} rejected.`,
      );
    } else if (outcome.stale) {
      // Stale row (409/404): close, surface the message, and refresh the queue.
      setPending(null);
      setStaleMsg(outcome.message);
      refetch();
    } else {
      // Fatal error stays in the modal via the hook's per-action state below.
      setStaleMsg(outcome.message);
    }
  }, [pending, approve, reject, showSuccess, refetch]);

  return (
    <>
      {/* Toolbar: count + env switcher */}
      <div className="flex items-center gap-2 sm:gap-4 mb-6 flex-wrap">
        <div className="flex-1 min-w-[160px]">
          <h2 className="text-sm font-semibold text-gray-300">Pending venue claims</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Seeded-listing claims that couldn&apos;t auto-verify. Approve transfers the venue; reject
            leaves it unchanged.
          </p>
        </div>
        {!loading && claims.length > 0 && (
          <span className="text-sm text-gray-500">{claims.length} awaiting review</span>
        )}

        {/* Env switcher */}
        <div className="flex items-center gap-1.5 p-1 bg-gray-800/80 border border-gray-700 rounded-xl">
          {(['dev', 'staging', 'prod'] as Env[]).map(env => {
            const isConfigured = Boolean(getAdminApiUrl(env));
            const isActive = selectedEnv === env;
            return (
              <button
                key={env}
                onClick={() => isConfigured && handleEnvChange(env)}
                title={isConfigured ? undefined : 'Not configured'}
                disabled={!isConfigured}
                className={`px-3 py-1 text-xs font-medium rounded-lg border transition-all capitalize ${
                  isActive
                    ? `${ENV_COLORS[env].pill} border-opacity-100`
                    : isConfigured
                      ? 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-700/60'
                      : 'text-gray-600 border-transparent cursor-not-allowed opacity-40'
                }`}
              >
                {env}
              </button>
            );
          })}
        </div>

        {selectedEnv === 'prod' && (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold tracking-wide ${ENV_COLORS.prod.badge}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
            PRODUCTION
          </span>
        )}
      </div>

      {successMsg && <SuccessBanner message={successMsg} />}
      {staleMsg && <div className="mb-4"><ErrorBanner message={staleMsg} /></div>}
      {error && <div className="mb-4"><ErrorBanner message={error} /></div>}

      <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
        {/* Header row */}
        <div className="hidden sm:flex items-center gap-4 px-5 py-3 border-b border-gray-800">
          <p className="flex-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">Venue</p>
          <p className="flex-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">Claimant</p>
          <p className="w-40 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider">Channel</p>
          <p className="w-24 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider">Requested</p>
          <div className="w-40 flex-shrink-0" />
        </div>

        {loading && (
          <div className="py-16 text-center flex flex-col items-center gap-3">
            <Spinner size="lg" />
            <p className="text-sm text-gray-500">Loading pending claims…</p>
          </div>
        )}
        {!loading && !error && claims.length === 0 && (
          <div className="py-16 text-center">
            <p className="text-gray-500 text-sm">No venue claims awaiting review. 🎉</p>
          </div>
        )}

        {!loading &&
          claims.map((claim, i) => {
            const isActing = actingId === claim.id;
            return (
              <div
                key={claim.id}
                className={`flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3 sm:py-4 hover:bg-gray-800/50 transition-colors ${
                  i < claims.length - 1 ? 'border-b border-gray-800' : ''
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{claim.businessName}</p>
                  <p className="text-xs text-gray-500 truncate font-mono">{claim.businessId}</p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-400 truncate font-mono">{claim.claimantUserId}</p>
                </div>
                <div className="w-40 flex-shrink-0">
                  <p className="text-sm text-gray-300 capitalize">{claim.method || '—'}</p>
                  <p className="text-xs text-gray-500 truncate">{claim.maskedDestination}</p>
                </div>
                <div className="w-24 flex-shrink-0">
                  <span className="text-xs text-gray-500 tabular-nums">{formatDate(claim.createdAt)}</span>
                </div>
                <div className="flex items-center gap-1.5 w-40 flex-shrink-0">
                  <button
                    onClick={() => setPending({ claim, action: 'approve' })}
                    disabled={isActing}
                    className="px-3 py-1.5 text-xs font-medium text-emerald-300 bg-emerald-950/30 border border-emerald-800/50 rounded-lg hover:text-white hover:border-emerald-500 hover:bg-emerald-900/40 transition-all disabled:opacity-40"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => setPending({ claim, action: 'reject' })}
                    disabled={isActing}
                    className="px-3 py-1.5 text-xs font-medium text-red-300 bg-red-950/30 border border-red-800/50 rounded-lg hover:text-white hover:border-red-500 hover:bg-red-900/40 transition-all disabled:opacity-40"
                  >
                    Reject
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      {pending && (
        <ClaimActionModal
          pending={pending}
          onClose={() => setPending(null)}
          onConfirm={handleConfirm}
          loading={actingId === pending.claim.id}
        />
      )}
    </>
  );
}
